// Server-driven analytics kill-switch (2026-08-06). Mirrors UploaderToggle
// exactly so the bootstrap path can read the current value synchronously
// before Redux is available.
//
// Storage tiers, highest precedence first:
//   1. localStorage["clipn:analytics-override"] — personal, per-browser
//      escape hatch for individual debugging (e.g. force session replay on
//      in dev to reproduce a specific bug). Not exposed in the admin UI;
//      set manually via DevTools. Wins over the env-wide server value so a
//      dev can preview locally without flipping it for everyone.
//   2. localStorage["clipn:analytics-server-cache"] — last-known
//      server value. Written by applyServerAnalyticsEnabled() on every
//      successful fetch/admin update. Missing = admin has never set it →
//      caller falls back to env default.
//
// The bootstrap module composes these with decideAnalyticsEnabled(env,
// override, serverValue) to produce the final boolean.

const CACHE_KEY = "clipn:analytics-server-cache";
const OVERRIDE_KEY = "clipn:analytics-override";

export const ANALYTICS_ON = "on";
export const ANALYTICS_OFF = "off";
const VALID = new Set([ANALYTICS_ON, ANALYTICS_OFF]);

const listeners = new Set();

const readStorage = (key) => {
  try {
    if (typeof localStorage === "undefined") return null;
    const v = localStorage.getItem(key);
    return v && VALID.has(v) ? v : null;
  } catch {
    return null;
  }
};

const writeStorage = (key, value) => {
  try {
    if (typeof localStorage !== "undefined") localStorage.setItem(key, value);
  } catch {
    /* private mode / quota — non-fatal */
  }
};

const clearStorage = (key) => {
  try {
    if (typeof localStorage !== "undefined") localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
};

export const readCachedAnalyticsEnabled = () => readStorage(CACHE_KEY);
export const readAnalyticsOverride = () => readStorage(OVERRIDE_KEY);

// Called by the systemSettings slice when the server returns a new value.
// A null/undefined value means "admin has not set env-wide, fall back to
// env default" — clear the cache so a subsequent bootstrap consults the
// env default rather than a stale server value.
export const applyServerAnalyticsEnabled = (value) => {
  if (value === null || value === undefined) {
    clearStorage(CACHE_KEY);
  } else if (VALID.has(value)) {
    writeStorage(CACHE_KEY, value);
  } else {
    return;
  }
  listeners.forEach((listener) => {
    try {
      listener(value ?? null);
    } catch {
      /* isolated per-subscriber */
    }
  });
};

export const subscribeAnalyticsEnabled = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};
